/** Admin > Hosting: what benleaf costs on Fly, day by day and item by item, and changing the server's size. */
import { Check, Copy, Minus, Plus, RefreshCw } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Field, Modal, Spinner } from "../components/ui";
import { api, ApiError, errorMessage } from "../lib/api";
import { formatDate, toast } from "../lib/app";
import { formatBytes, fullHosting, money, monthName, refresh, URLS, useResource, type CpuKind, type Hosting, type HostingResponse, type Month } from "./data";
import { FlySetup } from "./Storage";
import { Card, Loading, Notice, PageHead } from "./ui";

/** What the planner is pricing: how many servers of which size, the volume, and how long they run each day. */
interface Plan {
  servers: number;
  size: string;
  memoryMb: number;
  volumeGb: number;
  hours: number;
}

const GB = 2 ** 30;
const DAY_MS = 86_400_000;
const MAX_SERVERS = 10;
const MAX_VOLUME_GB = 500;

const memLabel = (mb: number) => (mb >= 1024 ? `${+(mb / 1024).toFixed(1)} GB` : `${mb} MB`);
const cpuLabel = (kind: CpuKind, cpus: number) => `${cpus} ${kind === "performance" ? "dedicated" : "shared"} CPU${cpus > 1 ? "s" : ""}`;
const tomlMemory = (mb: number) => (mb % 1024 === 0 ? `${mb / 1024}gb` : `${mb}mb`);
const dayLabel = (t: number) => new Date(t).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

function duration(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 10 ? `${h} h ${m % 60} min` : `${Math.round(seconds / 3600)} h`;
}

/** A stretch of time, for "the last …". */
function period(seconds: number): string {
  return seconds >= 2 * 86_400 ? `${Math.round(seconds / 86_400)} days` : duration(seconds);
}

function hoursLabel(h: number): string {
  if (h >= 23.95) return "all day";
  return `${+h.toFixed(1)} h a day`;
}

function currentPlan(h: Hosting): Plan {
  const m = h.machines.find((x) => x.isThis) ?? h.machines[0];
  return {
    servers: Math.max(1, h.machines.length),
    size: m?.size ?? "performance-1x",
    memoryMb: m?.memoryMb ?? 2048,
    volumeGb: h.volume.gb,
    hours: (h.month.duty ?? 1) * 24,
  };
}

/** Dollars per second for a plan: while running, while stopped, and storage (billed all the time). */
function planRates(h: Hosting, p: Plan) {
  const size = h.sizes.find((s) => s.name === p.size);
  const perSecond = size?.memory.find((m) => m.mb === p.memoryMb)?.perSecond ?? h.machines.find((m) => m.isThis)?.perSecond ?? 0;
  const month = h.rates.monthSeconds;
  // Each extra machine would need its own volume (and snapshots of it).
  const perVolume = p.volumeGb * (h.rates.volumeGbMonth / month) + h.live.snapshots / Math.max(1, h.volume.count);
  return {
    running: p.servers * perSecond,
    stopped: p.servers * ((h.rootfsBytes ?? 0) / GB) * (h.rates.rootfsGbMonth / month),
    storage: p.servers * perVolume,
  };
}

const blended = (r: ReturnType<typeof planRates>, share: number) => r.storage + share * r.running + (1 - share) * r.stopped;

export function HostingPage() {
  const hosting = useResource<HostingResponse>(URLS.hosting);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [restart, setRestart] = useState<{ size: string; memoryMb: number; since: number } | null>(null);
  const [done, setDone] = useState<{ size: string; memoryMb: number } | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Not while the tab is hidden: a forgotten tab would keep the server from stopping.
    const poll = setInterval(() => document.visibilityState === "visible" && refresh(URLS.hosting), 30_000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, []);

  const h = hosting.data;
  const full = fullHosting(h);
  const base = full ? currentPlan(full) : null;
  // Start the planner from what's running, and again whenever that changes (e.g. after a resize).
  const baseKey = base ? `${base.servers}/${base.size}/${base.memoryMb}/${base.volumeGb}` : "";
  useEffect(() => {
    if (base) setPlan(base);
  }, [baseKey]);

  return (
    <>
      <PageHead
        title="Hosting"
        lede={`What benleaf costs on Fly${full?.region ? ` in ${full.region}` : ""}, and how big its server is. Prices are in US dollars.`}
        actions={
          <button className="btn" onClick={() => hosting.reload()} disabled={hosting.loading} title="Ask Fly again">
            {hosting.loading ? <Spinner size={12} /> : <RefreshCw size={14} />} Refresh
          </button>
        }
      />

      {hosting.error && <Notice tone="bad">{hosting.error}</Notice>}
      {!h ? (
        !hosting.error && <Loading>Asking Fly…</Loading>
      ) : !h.app ? (
        <Notice>This server isn't running on Fly, so there's nothing to price. Deploy it with the steps in DEPLOY.md first.</Notice>
      ) : !full || !plan || !base ? null : (
        <Dashboard
          h={full}
          plan={plan}
          base={base}
          now={now}
          setPlan={setPlan}
          onApply={() => setConfirming(true)}
          done={done}
          onDismissDone={() => setDone(null)}
        />
      )}

      {confirming && full && plan && base && (
        <ApplyDialog
          h={full}
          plan={plan}
          base={base}
          onClose={() => setConfirming(false)}
          onResizing={(target) => {
            setConfirming(false);
            setRestart(target);
          }}
          onGrown={() => {
            setConfirming(false);
            hosting.reload();
            refresh(URLS.storage);
            refresh(URLS.volume);
          }}
        />
      )}
      {restart && (
        <RestartWatch
          target={restart}
          onDone={(r) => {
            setRestart(null);
            if (r) {
              hosting.update(() => r);
              setDone(restart);
              toast(`benleaf is now running on ${restart.size} with ${memLabel(restart.memoryMb)}`, "success");
            } else {
              toast("Couldn't confirm the new size after 5 minutes. Check `fly status` in your terminal.", "error");
              hosting.reload();
            }
          }}
        />
      )}
    </>
  );
}

function Dashboard({
  h,
  plan,
  base,
  now,
  setPlan,
  onApply,
  done,
  onDismissDone,
}: {
  h: Hosting;
  plan: Plan;
  base: Plan;
  now: number;
  setPlan: (p: Plan) => void;
  onApply: () => void;
  done: { size: string; memoryMb: number } | null;
  onDismissDone: () => void;
}) {
  const month = h.month;
  const sec = h.rates.monthSeconds;
  const baseRates = planRates(h, base);
  const planned = planRates(h, plan);
  const changed = plan.servers !== base.servers || plan.size !== base.size || plan.memoryMb !== base.memoryMb || plan.volumeGb !== base.volumeGb;
  const hoursChanged = Math.abs(plan.hours - base.hours) > 0.01;

  // Accrued ticks up between refreshes at the live rate.
  const accrued = month.accrued.total + (Math.max(0, Math.min(now, month.end) - h.at) / 1000) * h.live.perSecond;
  const running = h.machines.filter((m) => m.running).length;
  const thisMachine = h.machines.find((m) => m.isThis);
  const name = monthName(month.start);

  return (
    <>
      {h.flyError && (
        <Notice tone="warn">
          Couldn't ask Fly: {h.flyError} The figures below use this server's own records{h.volume.source === "disk" ? " and the disk size" : ""}.
        </Notice>
      )}
      {!h.apiEnabled && (
        <Card title="Connect to Fly">
          <FlySetup app={h.app!} what="resize the server, grow the volume and take snapshots" />
        </Card>
      )}

      <div className="stats stats-3">
        <div className="stat" title="What this calendar month (UTC, as Fly bills it) has cost so far. It counts up live at the current rate.">
          <span className="stat-label">{name} so far</span>
          <span className="stat-value">
            <Ticker value={accrued} />
          </span>
          <span className="stat-sub">
            Since {formatDate(month.billedSince)}
            {month.accrued.estimated > 0.005 && ` · ${money(month.accrued.estimated)} of it estimated`}
          </span>
        </div>
        <div className="stat" title="So far this month, plus the rest of it at the running time measured so far.">
          <span className="stat-label">Expected for {name}</span>
          <span className="stat-value">{money(month.expected)}</span>
          <span className="stat-sub">If it keeps running {hoursLabel(base.hours)}, as measured</span>
        </div>
        <div className="stat" title="What a 30-day month would cost if everything stayed exactly as it is right now, including whether the server is running.">
          <span className="stat-label">Rate right now</span>
          <span className="stat-value">
            {money(h.live.perSecond * sec)}
            <span className="stat-unit"> a month</span>
          </span>
          <span className="stat-sub">
            {running} of {h.machines.length} server{h.machines.length === 1 ? "" : "s"} running · {money(h.live.perSecond * 3600)} an hour
          </span>
        </div>
      </div>

      {done && <DoneNotice target={done} cpus={thisMachine?.cpus ?? 1} maxCompiles={h.maxConcurrentCompiles} onClose={onDismissDone} />}

      <Card title="Day by day" sub={`${name}, in UTC days as Fly bills them`}>
        <MonthChart
          month={month}
          at={h.at}
          projectedPerSecond={changed || hoursChanged ? blended(planned, plan.hours / 24) : blended(baseRates, base.hours / 24)}
          allDayPerSecond={planned.running + planned.storage}
          planned={changed || hoursChanged}
        />
      </Card>

      <Card title="What you're paying for" flush>
        <Breakdown h={h} />
      </Card>

      <Planner h={h} plan={plan} base={base} setPlan={setPlan} onApply={onApply} changed={changed} hoursChanged={hoursChanged} />
    </>
  );
}

/** Dollars and cents, with two more digits so it visibly counts up. */
function Ticker({ value }: { value: number }) {
  const cents = Math.floor(value * 100) / 100;
  const extra = Math.floor(value * 10_000) % 100;
  return (
    <>
      {money(cents)}
      <span className="ticker">{String(extra).padStart(2, "0")}</span>
    </>
  );
}

function Delta({ value }: { value: number }) {
  if (Math.abs(value) < 0.005) return null;
  return (
    <span className={`chip ${value > 0 ? "chip-warn" : "chip-ok"}`}>
      {value > 0 ? "+" : "−"}
      {money(Math.abs(value))}
    </span>
  );
}

function Planner({
  h,
  plan,
  base,
  setPlan,
  onApply,
  changed,
  hoursChanged,
}: {
  h: Hosting;
  plan: Plan;
  base: Plan;
  setPlan: (p: Plan) => void;
  onApply: () => void;
  changed: boolean;
  hoursChanged: boolean;
}) {
  const size = h.sizes.find((s) => s.name === plan.size);
  const sec = h.rates.monthSeconds;
  const thisMachine = h.machines.find((m) => m.isThis);
  // Keep an off-list size the machine already has, so the planner can start from it.
  const memory = size ? [...size.memory] : [];
  if (thisMachine && plan.size === base.size && !memory.some((m) => m.mb === thisMachine.memoryMb)) {
    memory.push({ mb: thisMachine.memoryMb, perSecond: thisMachine.perSecond });
    memory.sort((a, b) => a.mb - b.mb);
  }
  const set = (patch: Partial<Plan>) => setPlan({ ...plan, ...patch });
  const pickSize = (name: string) => {
    const s = h.sizes.find((x) => x.name === name)!;
    // Keep the memory if the new size offers it, else take its smallest that LaTeX can live with.
    const mb = s.memory.some((m) => m.mb === plan.memoryMb) ? plan.memoryMb : (s.memory.find((m) => m.mb >= h.minMemoryMb) ?? s.memory.at(-1)!).mb;
    set({ size: name, memoryMb: mb });
  };

  const resize = plan.size !== base.size || plan.memoryMb !== base.memoryMb;
  const grow = plan.volumeGb > base.volumeGb;
  const blocked = !h.apiEnabled
    ? "Connect Fly (above) to make changes from here."
    : plan.servers !== 1
      ? "benleaf can only run as one server."
      : plan.memoryMb < h.minMemoryMb
        ? `LaTeX needs at least ${memLabel(h.minMemoryMb)} of memory.`
        : "";

  const baseRates = planRates(h, base);
  const planned = planRates(h, plan);
  const share = plan.hours / 24;
  const remaining = Math.max(0, h.month.end - h.at) / 1000;
  const rows: Array<{ label: string; now: number; after: number }> = [
    { label: "A month, always on", now: (baseRates.running + baseRates.storage) * sec, after: (planned.running + planned.storage) * sec },
    { label: `A month at ${hoursLabel(plan.hours)}`, now: blended(baseRates, share) * sec, after: blended(planned, share) * sec },
    {
      label: `${monthName(h.month.start)} in all`,
      now: h.month.expected,
      after: changed || hoursChanged ? h.month.accrued.total + remaining * blended(planned, share) : h.month.expected,
    },
  ];
  const comparing = changed || hoursChanged;

  const groups: Array<[string, CpuKind]> = [
    ["Dedicated CPUs (steady speed for long compiles)", "performance"],
    ["Shared CPUs (cheaper, slowed down after bursts)", "shared"],
  ];

  return (
    <Card
      title="Server size"
      sub={`Running ${thisMachine?.size ?? base.size} with ${memLabel(base.memoryMb)}${thisMachine ? `, ${cpuLabel(thisMachine.cpuKind, thisMachine.cpus)}` : ""}. Price another size here, then apply it.`}
      actions={
        comparing && (
          <button className="btn btn-sm btn-ghost" onClick={() => setPlan(base)}>
            Back to what's running
          </button>
        )
      }
    >
      <div className="planner">
        <div className="planner-controls">
          <Field label="Performance" hint={size ? cpuLabel(size.cpuKind, size.cpus) : undefined}>
            <select value={plan.size} onChange={(e) => pickSize(e.target.value)}>
              {groups.map(([label, kind]) => (
                <optgroup key={kind} label={label}>
                  {h.sizes
                    .filter((s) => s.cpuKind === kind)
                    .map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.name} · from {money(s.memory[0].perSecond * sec)}/mo
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </Field>
          <Field label="Memory">
            <select value={plan.memoryMb} onChange={(e) => set({ memoryMb: Number(e.target.value) })}>
              {memory.map((m) => (
                <option key={m.mb} value={m.mb}>
                  {memLabel(m.mb)} · {money(m.perSecond * sec)}/mo{m.mb < h.minMemoryMb ? " · too small for LaTeX" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Volume (GB)" hint={`Now ${base.volumeGb} GB. It can grow but never shrink.`}>
            <input
              type="number"
              min={base.volumeGb}
              max={MAX_VOLUME_GB}
              value={plan.volumeGb}
              onChange={(e) => set({ volumeGb: Math.min(MAX_VOLUME_GB, Math.max(base.volumeGb, Math.round(Number(e.target.value) || 0))) })}
            />
          </Field>
          <Field label="Servers" hint="For pricing only">
            <div className="stepper">
              <button className="btn btn-sm" aria-label="One fewer server" disabled={plan.servers <= 1} onClick={() => set({ servers: plan.servers - 1 })}>
                <Minus size={13} />
              </button>
              <span className="stepper-value">{plan.servers}</span>
              <button
                className="btn btn-sm"
                aria-label="One more server"
                disabled={plan.servers >= MAX_SERVERS}
                onClick={() => set({ servers: plan.servers + 1 })}
              >
                <Plus size={13} />
              </button>
            </div>
          </Field>
          <Field
            label={`Running time: ${hoursLabel(plan.hours)}`}
            hint={
              h.month.duty === null
                ? "Not enough history yet, so this assumes it never stops."
                : `It ran ${Math.round(h.month.duty * 100)}% of the last ${period(h.month.dutySpanSeconds)}. It stops by itself when nobody has a project open.`
            }
          >
            <div className="row planner-hours">
              <input
                type="range"
                min={0}
                max={24}
                step={0.5}
                value={plan.hours}
                onChange={(e) => set({ hours: Number(e.target.value) })}
                aria-label="Hours running a day"
              />
              <button className="btn btn-sm" onClick={() => set({ hours: 24 })} disabled={plan.hours >= 24}>
                Always on
              </button>
              {h.month.duty !== null && (
                <button className="btn btn-sm" onClick={() => set({ hours: base.hours })} disabled={Math.abs(plan.hours - base.hours) < 0.01}>
                  As measured
                </button>
              )}
            </div>
          </Field>
        </div>

        <div className="planner-result">
          <table className="planner-compare">
            <thead>
              <tr>
                <th />
                <th className="num">Now</th>
                {comparing && <th className="num">With changes</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td className="num">{money(r.now)}</td>
                  {comparing && (
                    <td className="num">
                      <b>{money(r.after)}</b> <Delta value={r.after - r.now} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="planner-notes">
            {plan.servers > 1 && (
              <li className="note-warn">
                <b>Pricing only.</b> benleaf keeps live editing in memory and all its data on one volume, so a second server would split your projects in two.
                Each extra server above is priced with its own {plan.volumeGb} GB volume.
              </li>
            )}
            {size?.cpuKind === "shared" && (
              <li>Shared CPUs are throttled once their burst allowance is used up, so a long thesis compile can slow to a crawl or time out.</li>
            )}
            {size && size.cpus > h.maxConcurrentCompiles && (
              <li>
                With {size.cpus} CPUs you can also raise <code>MAX_CONCURRENT_COMPILES</code> (now {h.maxConcurrentCompiles}) in fly.toml so more people can
                compile at once.
              </li>
            )}
            {resize && !blocked && (
              <li>Resizing restarts benleaf, which takes about a minute. People editing reconnect by themselves and keep unsent changes.</li>
            )}
          </ul>
          <div className="row planner-apply">
            <span className="grow muted small">{blocked && (resize || grow || plan.servers !== base.servers) ? blocked : ""}</span>
            <button className="btn btn-primary" disabled={!!blocked || !(resize || grow)} onClick={onApply}>
              {resize && grow
                ? "Resize and grow volume…"
                : grow
                  ? `Grow volume to ${plan.volumeGb} GB…`
                  : resize
                    ? `Resize to ${plan.size}, ${memLabel(plan.memoryMb)}…`
                    : "Apply changes"}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ApplyDialog({
  h,
  plan,
  base,
  onClose,
  onResizing,
  onGrown,
}: {
  h: Hosting;
  plan: Plan;
  base: Plan;
  onClose: () => void;
  onResizing: (target: { size: string; memoryMb: number; since: number }) => void;
  onGrown: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const resize = plan.size !== base.size || plan.memoryMb !== base.memoryMb;
  const grow = plan.volumeGb > base.volumeGb;
  const sec = h.rates.monthSeconds;
  const before = planRates(h, { ...base, servers: 1 });
  const after = planRates(h, { ...plan, servers: 1 });

  async function apply() {
    setBusy(true);
    const since = Date.now();
    try {
      if (grow) await api.post("/api/admin/storage/volume/extend", { sizeGb: plan.volumeGb });
      if (!resize) {
        toast(`The volume is now ${plan.volumeGb} GB`, "success");
        onGrown();
        return;
      }
      try {
        await api.post("/api/admin/hosting/scale", { size: plan.size, memoryMb: plan.memoryMb });
      } catch (e) {
        // A refusal is a 4xx. Anything else is most likely the restart cutting the reply off; the watcher checks.
        if (e instanceof ApiError && e.status < 500) throw e;
      }
      onResizing({ size: plan.size, memoryMb: plan.memoryMb, since });
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={resize ? "Resize the server" : "Grow the volume"}
      onClose={onClose}
      width={540}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={apply}>
            {busy && <Spinner size={12} />} {resize ? "Resize and restart" : "Grow volume"}
          </button>
        </>
      }
    >
      <div className="stack">
        <table className="table compare-table">
          <thead>
            <tr>
              <th />
              <th>Now</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            {resize && (
              <tr>
                <td>Server</td>
                <td>
                  {base.size}, {memLabel(base.memoryMb)}
                </td>
                <td>
                  <b>
                    {plan.size}, {memLabel(plan.memoryMb)}
                  </b>
                </td>
              </tr>
            )}
            {grow && (
              <tr>
                <td>Volume</td>
                <td>{base.volumeGb} GB</td>
                <td>
                  <b>{plan.volumeGb} GB</b>
                </td>
              </tr>
            )}
            <tr>
              <td>Per month, always on</td>
              <td>{money((before.running + before.storage) * sec)}</td>
              <td>
                <b>{money((after.running + after.storage) * sec)}</b>
              </td>
            </tr>
          </tbody>
        </table>
        {grow && (
          <p className="muted small" style={{ margin: 0 }}>
            Volumes can't shrink again, and Fly bills for the full size whether or not it's used.
          </p>
        )}
        {resize && (
          <>
            <p style={{ margin: 0 }}>
              benleaf restarts on the new size, which takes about a minute. People editing are reconnected and keep their unsent changes.
            </p>
            <p className="muted small" style={{ margin: 0 }}>
              <code>fly deploy</code> puts the server back to the size in fly.toml, so change the <code>[[vm]]</code> section there too:
            </p>
            <Snippet text={`[[vm]]\n  size = "${plan.size}"\n  memory = "${tomlMemory(plan.memoryMb)}"`} />
          </>
        )}
      </div>
    </Modal>
  );
}

/** Waits for the machine to come back from its restart on the new size. */
function RestartWatch({ target, onDone }: { target: { size: string; memoryMb: number; since: number }; onDone: (h: Hosting | null) => void }) {
  const [waited, setWaited] = useState(0);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      if (stopped) return;
      setWaited(Date.now() - target.since);
      try {
        const res = await fetch(URLS.hosting, { credentials: "same-origin", signal: AbortSignal.timeout(10_000) });
        if (res.ok) {
          const r = (await res.json()) as Hosting;
          const m = r.machines?.find((x) => x.isThis);
          // A server that started after the resize, on the new size.
          if (r.startedAt > target.since && m?.size === target.size && m.memoryMb === target.memoryMb) return onDone(r);
        }
      } catch {}
      if (Date.now() - target.since > 5 * 60_000) return onDone(null);
      timer = setTimeout(check, 3000);
    };
    timer = setTimeout(check, 3000);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);
  return (
    <Modal title="Restarting benleaf" onClose={() => {}}>
      <div className="stack">
        <p className="row" style={{ margin: 0 }}>
          <Spinner /> Moving to {target.size} with {memLabel(target.memoryMb)}…
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          {waited < 15_000 ? "Fly is stopping the server." : "Waiting for it to come back up."} This page carries on by itself.
        </p>
      </div>
    </Modal>
  );
}

function DoneNotice({
  target,
  cpus,
  maxCompiles,
  onClose,
}: {
  target: { size: string; memoryMb: number };
  cpus: number;
  maxCompiles: number;
  onClose: () => void;
}) {
  return (
    <Notice
      tone="ok"
      action={
        <button className="icon-btn" onClick={onClose} aria-label="Dismiss">
          ×
        </button>
      }
    >
      <div className="stack" style={{ gap: 8 }}>
        <b>
          Now running on {target.size} with {memLabel(target.memoryMb)}. Put it in fly.toml so the next deploy keeps it:
        </b>
        <Snippet text={`[[vm]]\n  size = "${target.size}"\n  memory = "${tomlMemory(target.memoryMb)}"`} />
        {cpus > maxCompiles && (
          <span className="muted small">
            It has {cpus} CPUs now, so you could also set <code>MAX_CONCURRENT_COMPILES = "{cpus}"</code> under <code>[env]</code>.
          </span>
        )}
      </div>
    </Notice>
  );
}

function Snippet({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="snippet">
      <pre>{text}</pre>
      <button
        className="btn btn-sm"
        onClick={async () => {
          await navigator.clipboard.writeText(text).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- chart

/** Round tick steps (1, 2, 2.5, 5 × 10^n) so the axis reads $0, $0.50, $1.00… */
function niceStep(max: number, count = 4): number {
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
}

function MonthChart({
  month,
  at,
  projectedPerSecond,
  allDayPerSecond,
  planned,
}: {
  month: Month;
  at: number;
  projectedPerSecond: number;
  /** Dollars per second with the server never stopping, for the hat on each bar. */
  allDayPerSecond: number;
  planned: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const count = Math.round((month.end - month.start) / DAY_MS);
  const bars = Array.from({ length: count }, (_, i) => {
    const start = month.start + i * DAY_MS;
    const d = month.days[i];
    const measured = d ? d.running + d.stopped + d.storage : 0;
    const estimated = d?.estimated ?? 0;
    const ahead = Math.max(0, start + DAY_MS - Math.max(start, at)) / 1000;
    const future = ahead * projectedPerSecond;
    const allDay = (d?.allDay ?? 0) + ahead * allDayPerSecond;
    return {
      start,
      measured,
      light: estimated + future,
      estimated,
      future,
      allDay,
      runningSeconds: d?.runningSeconds ?? 0,
      today: at >= start && at < start + DAY_MS,
    };
  });
  const max = Math.max(0.01, ...bars.map((b) => Math.max(b.measured + b.light, b.allDay)));
  const step = niceStep(max);
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const pct = (v: number) => `${(v / top) * 100}%`;
  const shown = hover === null ? null : bars[hover];
  const today = bars.findIndex((b) => b.today);
  // Every fifth day, but none crowding "Today".
  const xLabel = (i: number) => (i === today ? "Today" : (i === 0 || (i + 1) % 5 === 0) && (today < 0 || Math.abs(i - today) > 2) ? i + 1 : "");

  return (
    <div className="hchart-wrap">
      <ul className="hchart-legend">
        <li>
          <i className="swatch hc-solid" /> Measured
        </li>
        <li>
          <i className="swatch hc-light" /> {planned ? "Expected with your changes" : "Expected"}, and estimated before tracking began
        </li>
        <li>
          <i className="tip-key hc-cap" /> If it ran all 24 hours
        </li>
      </ul>
      <div className="hchart">
        <div className="hchart-axis" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t} style={{ bottom: pct(t) }}>
              {money(t)}
            </span>
          ))}
        </div>
        <div className="hchart-plot" onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <i key={t} className="hchart-grid" style={{ bottom: pct(t) }} />
          ))}
          <div className="hchart-cols">
            {bars.map((b, i) => (
              <div
                key={b.start}
                className={`hchart-col${b.today ? " today" : ""}${hover === i ? " on" : ""}`}
                tabIndex={0}
                aria-label={`${dayLabel(b.start)}: ${money(b.measured)} measured, ${money(b.light)} expected or estimated, ${money(b.allDay)} if it ran all day`}
                onMouseEnter={() => setHover(i)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
              >
                <span className="hchart-bar">
                  {b.light > 0 && <span className="hchart-seg hc-light" style={{ height: pct(b.light) }} />}
                  {b.measured > 0 && <span className="hchart-seg hc-solid" style={{ height: pct(b.measured) }} />}
                  {b.allDay > 0 && <span className="hchart-cap" style={{ bottom: pct(b.allDay) }} />}
                </span>
              </div>
            ))}
          </div>
          {shown && (
            <div
              className={`hchart-tip${hover! < 4 ? " left" : hover! > count - 5 ? " right" : ""}`}
              style={{ left: `${((hover! + 0.5) / count) * 100}%`, bottom: `calc(${pct(Math.max(shown.measured + shown.light, shown.allDay))} + 8px)` }}
            >
              <b>{dayLabel(shown.start)}</b>
              {shown.measured > 0 && (
                <span>
                  <i className="tip-key hc-solid" /> Measured {money(shown.measured)}
                </span>
              )}
              {shown.estimated > 0.0001 && (
                <span>
                  <i className="tip-key hc-light" /> Estimated {money(shown.estimated)}
                </span>
              )}
              {shown.future > 0.0001 && (
                <span>
                  <i className="tip-key hc-light" /> Expected {money(shown.future)}
                </span>
              )}
              {shown.allDay > 0 && (
                <span>
                  <i className="tip-key hc-cap" /> All 24 hours {money(shown.allDay)}
                </span>
              )}
              {shown.runningSeconds > 0 && <span className="muted">Server ran {duration(shown.runningSeconds)}</span>}
            </div>
          )}
        </div>
        <div className="hchart-x" aria-hidden="true">
          {bars.map((b, i) => (
            <span key={b.start} className={b.today ? "today" : ""}>
              {xLabel(i)}
            </span>
          ))}
        </div>
      </div>
      <details className="hchart-table">
        <summary className="muted small">Show as a table</summary>
        <div className="admin-scroll">
          <table className="table admin-table">
            <thead>
              <tr>
                <th>Day (UTC)</th>
                <th className="num">Server ran</th>
                <th className="num">Measured</th>
                <th className="num">Estimated or expected</th>
                <th className="num">All 24 hours</th>
              </tr>
            </thead>
            <tbody>
              {bars.map((b) => (
                <tr key={b.start}>
                  <td>{dayLabel(b.start)}</td>
                  <td className="num">{b.runningSeconds ? duration(b.runningSeconds) : "–"}</td>
                  <td className="num">{b.measured ? money(b.measured) : "–"}</td>
                  <td className="num">{b.light ? money(b.light) : "–"}</td>
                  <td className="num">{b.allDay ? money(b.allDay) : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------- breakdown

function Breakdown({ h }: { h: Hosting }) {
  const sec = h.rates.monthSeconds;
  const m = h.month;
  const storage = h.live.volume + h.live.snapshots;
  // The month's storage is one figure; split it the way it's billed now.
  const volumeSoFar = storage ? (m.accrued.storage * h.live.volume) / storage : m.accrued.storage;
  const snapshotsSoFar = m.accrued.storage - volumeSoFar;
  const rootfsMonth = ((h.rootfsBytes ?? 0) / GB) * h.rates.rootfsGbMonth;
  const tracked = m.trackedSince > m.billedSince;
  const note = (children: ReactNode) => <div className="muted small">{children}</div>;

  return (
    <>
      <div className="admin-scroll">
        <table className="table admin-table breakdown-table">
          <thead>
            <tr>
              <th>What</th>
              <th className="num">Per month</th>
              <th className="num">So far this month</th>
            </tr>
          </thead>
          <tbody>
            {h.machines.map((x) => (
              <tr key={x.id}>
                <td>
                  <b>Server</b> <span className="muted mono small">{x.name || x.id}</span>{" "}
                  <span className={`chip ${x.running ? "chip-ok" : ""}`}>
                    <i className="dot" /> {x.state}
                  </span>
                  {note(`${x.size ?? "custom size"} · ${cpuLabel(x.cpuKind, x.cpus)} · ${memLabel(x.memoryMb)} · ${x.region}`)}
                </td>
                <td className="num">
                  {money(x.perSecond * sec)}
                  {note("while running")}
                </td>
                <td className="num">
                  {x.isThis ? money(m.accrued.running) : <span className="muted">–</span>}
                  {x.isThis && note(`ran ${duration(m.runningSeconds)}`)}
                </td>
              </tr>
            ))}
            {h.rootfsBytes !== null && (
              <tr>
                <td>
                  <b>Stopped server</b>
                  {note(`Its ${formatBytes(h.rootfsBytes)} image, billed while it's stopped`)}
                </td>
                <td className="num">
                  {money(rootfsMonth)}
                  {note("while stopped")}
                </td>
                <td className="num">{money(m.accrued.stopped)}</td>
              </tr>
            )}
            <tr>
              <td>
                <b>Volume</b>
                {note(
                  `${h.volume.gb} GB${h.volume.count > 1 ? ` across ${h.volume.count} volumes` : ""} at ${money(h.rates.volumeGbMonth)} a GB${h.volume.source === "disk" ? " (size read from the disk)" : ""}`,
                )}
              </td>
              <td className="num">{money(h.live.volume * sec)}</td>
              <td className="num">{money(volumeSoFar)}</td>
            </tr>
            <tr>
              <td>
                <b>Snapshots</b>
                {note(
                  h.snapshots
                    ? `${h.snapshots.count} kept, ${formatBytes(h.snapshots.bytes)} in all. The first ${h.rates.snapshotFreeGb} GB a month are free.`
                    : "Unknown without the Fly token.",
                )}
              </td>
              <td className="num">{h.snapshots ? (h.live.snapshots ? money(h.live.snapshots * sec) : "Free") : "–"}</td>
              <td className="num">{h.snapshots ? money(snapshotsSoFar) : "–"}</td>
            </tr>
            {m.accrued.estimated > 0 && (
              <tr>
                <td>
                  <b>Before tracking began</b>
                  {note(`${formatDate(m.billedSince)} to ${formatDate(m.trackedSince)}, estimated from how much the server has run since`)}
                </td>
                <td className="num muted">–</td>
                <td className="num">{money(m.accrued.estimated)}</td>
              </tr>
            )}
            <tr className="total-row">
              <td colSpan={2}>Total so far</td>
              <td className="num">
                <b>{money(m.accrued.total)}</b>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="muted small admin-card-foot">
        Worked out from Fly's published prices for {h.region}
        {h.regionPriced ? (h.markup !== 1 ? ` (${h.markup.toFixed(2)}× the cheapest regions)` : "") : " (not in Fly's price list, so base prices)"}, per 30
        days, and this server's own record of when it ran{tracked ? `, which began ${formatDate(m.trackedSince)}` : ""}. Not counted: outbound data (about
        US$0.04 a GB from Sydney) and anything else in your Fly organisation. Fly's invoice is the real number:{" "}
        <a href="https://fly.io/dashboard/personal/billing" target="_blank" rel="noreferrer">
          fly.io billing
        </a>
        .
      </p>
    </>
  );
}
